import { useCallback, useEffect, useRef, useState } from 'react';
import { useListingField, ListingResource } from '@brightmls/nextgen.lae.flc';
import { PropertyField } from '../../../../system/field';
import { useMetadata } from '../../../../contexts/MetadataContext';
import { useService } from '../../../../contexts/ServiceContext';
import { LookupOption, DynamicLookupConfig } from './types';

interface UseDynamicLookupProps {
  dynamicLookup?: DynamicLookupConfig;
  onFieldValueChange: (value: any) => void;
}

export const useDynamicLookup = ({
  dynamicLookup,
  onFieldValueChange,
}: UseDynamicLookupProps) => {
  const { getFieldByLocaleKey } = useMetadata();
  const { searchLookups } = useService();
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<LookupOption[]>([]);
  const previousParentValue = useRef<string>('');

  const lookupParent = dynamicLookup?.lookupParent || '';
  const context = dynamicLookup?.context;
  const standardName = context?.standardName || '';

  // Get the parent field metadata to resolve the correct field Key
  const parentFieldMetadata = getFieldByLocaleKey(lookupParent);
  const parentFieldKey = parentFieldMetadata?.Key || '';

  const parentField = useListingField(
    ListingResource.Property,
    parentFieldKey as PropertyField,
    '',
  );

  const parentValue = parentField.value;

  // Load lookups based on parent value
  const loadLookups = useCallback(async () => {
    if (!parentValue) {
      setOptions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchLookups(
        standardName, // lookupName
        lookupParent, // parentLookupName
        parentValue, // parentLookupValue
      );

      const formattedOptions: LookupOption[] = results.map((item) => ({
        value: item.LookupValue || item.value,
        label: item.DisplayValue || item.LookupValue || item.value,
      }));

      setOptions(formattedOptions);
    } catch (error) {
      console.error('Error loading lookups:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [parentValue, lookupParent, standardName, searchLookups]);

  // Load lookups when parent value changes
  useEffect(() => {
    if (parentValue) {
      loadLookups();
    } else {
      setOptions([]);
    }
  }, [parentValue, loadLookups]);

  // Clear field value when parent value changes
  useEffect(() => {
    // Only clear if parent value actually changed and it's not the initial load
    if (
      previousParentValue.current !== '' &&
      previousParentValue.current !== parentValue
    ) {
      onFieldValueChange(null);
    }
    previousParentValue.current = parentValue;
  }, [parentValue, onFieldValueChange]);

  return {
    options,
    isLoading,
    parentValue,
    parentFieldMetadata,
    lookupParent,
  };
};
